import { CommentIndicator } from 'features/libra/comments/CommentIndicator'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { SaveGuardModal } from 'features/libra/SaveGuardModal'
import { FormSection } from 'features/libra/sections/sectionTypes'
import { useActiveStepId } from 'features/libra/ticket/TicketStateContext'
import { useRegisterSaveGuard } from 'features/libra/useRegisterSaveGuard'
import {
  LibraWorflowStepComponentProps,
  UseValidateConfigHook,
} from 'features/libra/workflow/workflowStepsApi'
import { PermissionsGate } from 'features/permissions'
import type { FC } from 'react'
import { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import {
  PERMISSION_GECKO_INTERNAL_MEMBER,
  PERMISSION_LIBRA_EXECUTIVE,
} from 'types'

import { AiAutofillBanner } from './GongAgentAutofill'
import { NiiBasicInformationCard } from './niiReport/cards/NiiBasicInformationCard'
import { NiiConclusionCard } from './niiReport/cards/NiiConclusionCard'
import { NiiInspectionEconomicsCard } from './niiReport/cards/NiiInspectionEconomicsCard'
import { NiiReportInfoCard } from './niiReport/cards/NiiReportInfoCard'
import {
  niiReportConfigSchema,
  NiiReportFields,
} from './niiReport/niiReportApi'
import {
  NiiReportDataProvider,
  useNiiReportData,
} from './niiReport/NiiReportDataContext'
import {
  NiiSaveBanner,
  StickySaveBannerContainer,
} from './niiReport/NiiSaveBanner'
import { NiiPdfExportButton } from './niiReport/pdf/NiiPdfExportButton'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`

const NiiReportContent: FC<{
  fields: NiiReportFields
  sections: FormSection[]
  zoneFieldName: string
  readonly?: boolean
}> = ({ fields, sections, zoneFieldName, readonly }) => {
  const stepId = useActiveStepId()
  const { hasChanges, doSave, resetChanges, isLoading, setLocalFieldValue } =
    useNiiReportData()
  const saveGuard = useRegisterSaveGuard(!readonly && hasChanges, doSave)

  const onFieldValues = useCallback(
    (fv: FieldValues) => {
      Object.entries(fv).forEach(([key, value]) =>
        setLocalFieldValue(key, value)
      )
    },
    [setLocalFieldValue]
  )

  const agentSections = useMemo(
    () => sections.filter((s) => s.agent !== null && s.agent !== undefined),
    [sections]
  )

  return (
    <Container>
      <SaveGuardModal {...saveGuard} />
      {!readonly && (
        <StickySaveBannerContainer>
          <NiiSaveBanner
            hasChanges={hasChanges}
            isLoading={isLoading}
            onReset={resetChanges}
            onSave={doSave}
          />
        </StickySaveBannerContainer>
      )}
      {!readonly &&
        agentSections.map((s) => (
          <AiAutofillBanner
            key={s.id}
            section={s}
            onFieldValues={onFieldValues}
          />
        ))}
      <CommentIndicator
        anchor={{ type: 'field', stepId, fieldId: fields.analysisDate }}>
        <NiiBasicInformationCard
          analysisDateField={fields.analysisDate}
          readonly={readonly}
        />
      </CommentIndicator>
      <CommentIndicator
        anchor={{ type: 'field', stepId, fieldId: fields.executiveSummary }}>
        <NiiReportInfoCard
          title='Executive Summary'
          fieldKey={fields.executiveSummary}
          readonly={readonly}
        />
      </CommentIndicator>
      <CommentIndicator
        anchor={{ type: 'field', stepId, fieldId: fields.internalComments }}>
        <NiiReportInfoCard
          title='Internal Assessment Comments and Notes'
          fieldKey={fields.internalComments}
          readonly={readonly}
        />
      </CommentIndicator>
      <CommentIndicator
        anchor={{ type: 'field', stepId, fieldId: fields.eligibility }}>
        <NiiConclusionCard
          eligibilityField={fields.eligibility}
          eligibilityComputedField={fields.eligibilityComputed}
          recommendationsField={fields.recommendations}
          readonly={readonly}
        />
      </CommentIndicator>
      <PermissionsGate
        requires={[
          PERMISSION_GECKO_INTERNAL_MEMBER,
          PERMISSION_LIBRA_EXECUTIVE,
        ]}>
        <CommentIndicator
          anchor={{ type: 'field', stepId, fieldId: fields.inspectableArea }}>
          <NiiInspectionEconomicsCard
            areaField={fields.inspectableArea}
            readonly={readonly}
          />
        </CommentIndicator>
      </PermissionsGate>
      <NiiPdfExportButton fields={fields} zoneFieldName={zoneFieldName} />
    </Container>
  )
}

export const useValidateConfig: UseValidateConfigHook = ({ config }) => {
  return useMemo(
    () => niiReportConfigSchema.safeParse(config).success,
    [config]
  )
}

export const Component: FC<
  LibraWorflowStepComponentProps<Record<string, unknown>>
> = ({ config, readonly }) => {
  const { fields, sections, zoneFieldName } = useMemo(
    () => niiReportConfigSchema.parse(config),
    [config]
  )

  return (
    <NiiReportDataProvider fields={fields}>
      <NiiReportContent
        fields={fields}
        sections={sections ?? []}
        zoneFieldName={zoneFieldName}
        readonly={readonly}
      />
    </NiiReportDataProvider>
  )
}
